/**
 * Falling notes utilities and calculations
 */

import type {
  FallingNote,
  BuildVisibleNotesArgs,
} from "./types";
import { clamp, lanesFromMask } from "./constants";
import { tickToMsFast, msToTickFast, lowerBoundByT0 } from "./tempoUtils";

/**
 * Build visible falling notes for the current position
 */
export function buildVisibleFallingNotes(
  args: BuildVisibleNotesArgs
): FallingNote[] {
  const { score, tempoIndex, posMs, lookaheadMs, pastMs, hitWindowMs } = args;
  const events = score.events ?? [];
  if (!events.length) return [];

  const ppq = score.ppq;

  // Geometry constants - 8 lanes side by side
  const STAGE_H = 320;
  const SPAWN_Y = 24;
  const PAD_AREA_H = 80;

  const startMs = posMs - pastMs;
  const endMs = posMs + lookaheadMs;

  const startTick = Math.max(0, msToTickFast(startMs, tempoIndex, ppq));
  const endTick = Math.max(0, msToTickFast(endMs, tempoIndex, ppq)) + 1;

  let idx = lowerBoundByT0(events, startTick);

  const notes: FallingNote[] = [];
  for (let i = idx; i < events.length; i++) {
    const e = events[i];
    if (e.t0 > endTick) break;

    const eventMs = tickToMsFast(e.t0, tempoIndex, ppq);
    const alphaRaw = (eventMs - posMs) / lookaheadMs;
    const alpha = clamp(alphaRaw, -pastMs / lookaheadMs, 1.1);

    const isHit = Math.abs(eventMs - posMs) <= hitWindowMs;

    const lanes = lanesFromMask(e.m);
    for (const lane of lanes) {
      // 8 lanes side by side - single row
      const col = lane; // 0-7 directly maps to column
      const row = 0;

      const padTop = STAGE_H - PAD_AREA_H;
      const targetY = padTop + PAD_AREA_H / 2;
      const y = targetY + (SPAWN_Y - targetY) * alpha;

      // 8 columns
      const colWidthPct = 100 / 8;
      const xPct = colWidthPct * (col + 0.5);

      let opacity = 1;
      if (alphaRaw < 0) {
        opacity = clamp(1 + alphaRaw * 6, 0, 1);
      } else {
        opacity = clamp(1 - (alphaRaw - 0.85) * 3, 0.2, 1);
      }

      if (y < -40 || y > STAGE_H + 60 || opacity <= 0.02) continue;

      notes.push({
        key: `${e.t0}-${lane}`,
        lane,
        col,
        row,
        y,
        xPct,
        isHit,
        opacity,
      });
    }
  }

  return notes;
}
